import { useState, useMemo, useCallback } from 'react';
import { Link } from 'react-router-dom';
import { SwalAlert } from '@/hooks/useSweetAlert';
import {
  providers as initialProviders,
  streamState as initialStreamState,
  socialStreams as initialSocialStreams,
  streamEvents,
  type ProviderConfig,
} from '@/mocks/broadcastCenter';
import type { StreamProviderId } from '@/types/streaming';
import { hostingTypeLabels } from '@/types/streaming';
import { radioStations } from '@/mocks/radios';

// ── Derive hosting type from mock data ──
const MOCK_HOSTING = radioStations[0]?.hostingType || 'external';

function timeAgo(iso: string): string {
  const diff = Date.now() - new Date(iso).getTime();
  const mins = Math.floor(diff / 60000);
  if (mins < 1) return 'Ahora';
  if (mins < 60) return `Hace ${mins} min`;
  const hrs = Math.floor(mins / 60);
  if (hrs < 24) return `Hace ${hrs}h`;
  const days = Math.floor(hrs / 24);
  if (days === 1) return 'Ayer';
  if (days < 7) return `Hace ${days} días`;
  return new Date(iso).toLocaleDateString('es-GT', { day: 'numeric', month: 'short' });
}

function fullDateTime(iso: string): string {
  return new Date(iso).toLocaleDateString('es-GT', {
    day: 'numeric', month: 'short', hour: '2-digit', minute: '2-digit',
  });
}

const statusMeta: Record<string, { label: string; dot: string; bg: string; text: string }> = {
  online: { label: 'Online', dot: 'bg-emerald-500', bg: 'bg-emerald-100', text: 'text-emerald-700' },
  offline: { label: 'Offline', dot: 'bg-amber-500', bg: 'bg-amber-100', text: 'text-amber-700' },
  reconectando: { label: 'Reconectando', dot: 'bg-amber-500', bg: 'bg-amber-100', text: 'text-amber-700' },
  verificando: { label: 'Verificando', dot: 'bg-sky-500', bg: 'bg-sky-100', text: 'text-sky-700' },
};

export default function AdminBroadcastCenter() {
  const [providers, setProviders] = useState<ProviderConfig[]>(initialProviders);
  const [streamState, setStreamState] = useState(initialStreamState);
  const [socialStreams, setSocialStreams] = useState(initialSocialStreams);
  const [editingUrl, setEditingUrl] = useState<Record<string, string>>({});

  // ── Mi señal: simple view state ──
  const [signalView, setSignalView] = useState<'simple' | 'avanzado'>('simple');
  const [testSignalLoading, setTestSignalLoading] = useState(false);

  const activeProvider = useMemo(
    () => providers.find((p) => p.active) || null,
    [providers],
  );

  const statusInfo = statusMeta[streamState.status];

  /* ── Mi señal: Probar señal ── */
  const handleTestSignal = useCallback(async () => {
    setTestSignalLoading(true);
    await new Promise((r) => setTimeout(r, 1500));
    setTestSignalLoading(false);
    const ok = Math.random() > 0.1;
    if (ok) {
      await SwalAlert.toast('Señal activa. Stream respondiendo correctamente.', 'success');
    } else {
      await SwalAlert.warning('Señal débil', 'El servidor no respondió. Verificá la URL del stream.');
    }
  }, []);

  /* ── Validar URL ── */
  const handleValidateUrl = useCallback((providerId: StreamProviderId) => {
    const url = editingUrl[providerId] || providers.find((p) => p.id === providerId)?.streamUrl || '';
    if (!url.trim()) {
      SwalAlert.warning('URL vacía', 'Ingresá la URL del stream para validarla.');
      return;
    }

    setProviders((prev) =>
      prev.map((p) => (p.id === providerId ? { ...p, validating: true } : p)),
    );

    setTimeout(() => {
      const isValid = url.includes('stream') || url.includes('live') || url.includes(':8000') || url.includes(':9000') || url.includes(':2199');
      setProviders((prev) =>
        prev.map((p) =>
          p.id === providerId
            ? { ...p, validated: isValid, lastValidated: new Date().toISOString(), validating: false, streamUrl: url }
            : p,
        ),
      );
      if (isValid) {
        SwalAlert.toast('URL validada correctamente. El servidor responde.', 'success');
      } else {
        SwalAlert.warning('No se pudo validar', 'El servidor no respondió. Verificá la URL y el puerto.');
      }
    }, 1500 + Math.random() * 1000);
  }, [editingUrl, providers]);

  const handleUrlChange = useCallback((providerId: string, value: string) => {
    setEditingUrl((prev) => ({ ...prev, [providerId]: value }));
  }, []);

  const handleActivateProvider = useCallback((providerId: StreamProviderId) => {
    const provider = providers.find((p) => p.id === providerId);
    if (!provider) return;
    if (provider.validated !== true) {
      SwalAlert.warning('Validá primero', 'Tenés que validar la URL antes de activar este proveedor.');
      return;
    }

    SwalAlert.confirm(
      `¿Activar ${provider.label}?`,
      `El stream cambiará al servidor ${provider.label}. Los oyentes no deberían notar la transición.`,
    ).then((result) => {
      if (result.isConfirmed) {
        setProviders((prev) =>
          prev.map((p) => ({ ...p, active: p.id === providerId })),
        );
        setStreamState((prev) => ({
          ...prev,
          currentProvider: providerId,
          streamUrl: provider.streamUrl,
          format: provider.format,
          bitrate: provider.bitrate,
          startedAt: new Date().toISOString(),
          uptime: '0m',
        }));
        SwalAlert.toast(`${provider.label} activado como proveedor principal`, 'success');
      }
    });
  }, [providers]);

  const handleSocialToggle = useCallback((channelId: string) => {
    setSocialStreams((prev) =>
      prev.map((c) => {
        if (c.id !== channelId) return c;
        const newActive = !c.active;
        return { ...c, active: newActive };
      }),
    );
    const channel = socialStreams.find((c) => c.id === channelId);
    if (channel) {
      const newState = !channel.active;
      SwalAlert.toast(
        `${channel.label}: ${newState ? 'Activado' : 'Desactivado'}`,
        newState ? 'success' : 'info',
      );
    }
  }, [socialStreams]);

  const [historyFilter, setHistoryFilter] = useState<string>('todos');

  const eventTypes = useMemo(() => {
    const types = new Set(streamEvents.map((e) => e.type));
    return ['todos', ...Array.from(types)];
  }, []);

  const typeLabels: Record<string, string> = {
    todos: 'Todos',
    stream_start: 'Inicio',
    stream_end: 'Fin',
    stream_error: 'Errores',
    stream_reconnect: 'Reconexión',
    facebook: 'Facebook',
    youtube: 'YouTube',
    url_validated: 'Validación',
    provider_switch: 'Cambios',
  };

  const filteredEvents = useMemo(() => {
    if (historyFilter === 'todos') return streamEvents;
    return streamEvents.filter((e) => e.type === historyFilter);
  }, [historyFilter]);

  return (
    <div className="min-h-screen bg-background-100">
      {/* Mobile topbar */}
      <header className="lg:hidden sticky top-0 z-30 bg-background-50/98 backdrop-blur-2xl border-b border-background-200">
        <div className="flex items-center justify-between px-4 h-14">
          <div className="flex items-center gap-2.5 pl-12">
            <div className={`w-7 h-7 rounded-lg flex items-center justify-center ${streamState.status === 'online' ? 'bg-emerald-500' : streamState.status === 'reconectando' ? 'bg-amber-500' : 'bg-foreground-400'}`}>
              <i className="ri-broadcast-line text-background-50 text-xs"></i>
            </div>
            <div>
              <h1 className="text-foreground-950 font-bold text-sm">Centro de Tx</h1>
              <p className="text-foreground-400 text-[9px]">{statusInfo.label} &bull; {streamState.listeners.toLocaleString()} oyentes</p>
            </div>
          </div>
          <Link
            to="/admin/dashboard"
            className="flex items-center gap-1.5 text-foreground-500 text-xs hover:text-foreground-700 transition-colors"
          >
            <i className="ri-arrow-left-line text-xs"></i>
            Volver
          </Link>
        </div>
      </header>

      <div className="flex">


        <main className="flex-1 min-w-0">
          {/* Desktop toolbar */}
          <div className="hidden lg:flex items-center justify-between h-16 px-8 border-b border-background-200 bg-background-50 sticky top-0 z-20">
            <div className="flex items-center gap-4">
              <div className={`w-9 h-9 rounded-xl flex items-center justify-center ${streamState.status === 'online' ? 'bg-emerald-100' : 'bg-foreground-100'}`}>
                <i className={`ri-broadcast-line text-base ${streamState.status === 'online' ? 'text-emerald-600' : 'text-foreground-400'}`}></i>
              </div>
              <div>
                <h2 className="text-foreground-950 font-bold text-base">Centro de Transmisión</h2>
                <p className="text-foreground-400 text-xs">
                  {statusInfo.label} &bull; {activeProvider?.label || 'Sin proveedor'} &bull; {streamState.listeners.toLocaleString()} oyentes
                </p>
              </div>
            </div>
          </div>

          <div className="p-4 md:p-6 lg:p-8 pb-8">
            <div className="max-w-[1100px] mx-auto space-y-6">

              {/* ═══════════ MI SEÑAL — Vista simple ═══════════ */}
              <section>
                <div className="flex items-center justify-between mb-3">
                  <h3 className="text-foreground-950 font-bold text-sm flex items-center gap-2">
                    <i className="ri-signal-tower-line text-accent-500"></i>
                    Mi señal
                  </h3>
                  <button
                    onClick={() => setSignalView(signalView === 'simple' ? 'avanzado' : 'simple')}
                    className="text-foreground-400 text-[10px] font-semibold hover:text-foreground-600 transition-colors cursor-pointer whitespace-nowrap"
                  >
                    {signalView === 'simple' ? 'Ver avanzado' : 'Ver simple'}
                    {signalView === 'simple' ? <i className="ri-arrow-down-s-line ml-1 text-[10px]"></i> : <i className="ri-arrow-up-s-line ml-1 text-[10px]"></i>}
                  </button>
                </div>

                {MOCK_HOSTING === 'external' ? (
                  /* ── Escenario: Ya tengo streaming ── */
                  <div className="bg-background-50 rounded-2xl border border-background-200 p-5">
                    <div className="flex items-center gap-3 mb-4">
                      <div className="w-10 h-10 rounded-xl bg-amber-100 flex items-center justify-center">
                        <i className="ri-link text-amber-600 text-lg"></i>
                      </div>
                      <div>
                        <h4 className="text-foreground-950 font-bold text-sm">Streaming externo conectado</h4>
                        <p className="text-foreground-400 text-[10px]">Tu stream está vinculado desde un proveedor externo</p>
                      </div>
                      <div className="ml-auto flex items-center gap-1.5">
                        <span className={`w-2 h-2 rounded-full ${streamState.status === 'online' ? 'bg-emerald-500 animate-pulse' : 'bg-amber-500'}`}></span>
                        <span className={`text-xs font-bold ${streamState.status === 'online' ? 'text-emerald-600' : 'text-amber-600'}`}>
                          {streamState.status === 'online' ? 'Online' : 'Offline'}
                        </span>
                      </div>
                    </div>

                    {/* URL + Estado */}
                    <div className="bg-background-100 rounded-xl p-3 mb-4">
                      <div className="flex items-center gap-2">
                        <code className="text-foreground-700 text-xs font-mono truncate flex-1">{streamState.streamUrl}</code>
                        <button
                          onClick={() => { navigator.clipboard.writeText(streamState.streamUrl); SwalAlert.toast('URL copiada', 'success'); }}
                          className="w-7 h-7 rounded-lg bg-background-200 flex items-center justify-center cursor-pointer hover:bg-background-300 shrink-0"
                        >
                          <i className="ri-file-copy-line text-foreground-400 text-xs"></i>
                        </button>
                      </div>
                    </div>

                    {/* Quick actions */}
                    <div className="grid grid-cols-2 sm:grid-cols-4 gap-2">
                      <button
                        onClick={handleTestSignal}
                        disabled={testSignalLoading}
                        className="flex items-center justify-center gap-1.5 px-3 py-2.5 rounded-xl bg-background-100 hover:bg-background-200 text-foreground-700 text-xs font-semibold transition-colors cursor-pointer whitespace-nowrap"
                      >
                        {testSignalLoading ? (
                          <><i className="ri-loader-4-line animate-spin"></i>Probando...</>
                        ) : (
                          <><i className="ri-radar-line"></i>Probar señal</>
                        )}
                      </button>
                      <button
                        onClick={() => { setEditingUrl({ ...editingUrl, [activeProvider?.id || streamState.currentProvider]: streamState.streamUrl }); }}
                        className="flex items-center justify-center gap-1.5 px-3 py-2.5 rounded-xl bg-background-100 hover:bg-background-200 text-foreground-700 text-xs font-semibold transition-colors cursor-pointer whitespace-nowrap"
                      >
                        <i className="ri-edit-line"></i>
                        Guardar URL
                      </button>
                      <button
                        onClick={() => SwalAlert.toast('Previsualización no disponible en este momento.', 'info')}
                        className="flex items-center justify-center gap-1.5 px-3 py-2.5 rounded-xl bg-background-100 hover:bg-background-200 text-foreground-700 text-xs font-semibold transition-colors cursor-pointer whitespace-nowrap"
                      >
                        <i className="ri-play-line"></i>
                        Reproducir preview
                      </button>
                      <button
                        onClick={() => setSignalView('avanzado')}
                        className="flex items-center justify-center gap-1.5 px-3 py-2.5 rounded-xl bg-primary-100 hover:bg-primary-200 text-primary-700 text-xs font-semibold transition-colors cursor-pointer whitespace-nowrap"
                      >
                        <i className="ri-settings-3-line"></i>
                        Configuración
                      </button>
                    </div>
                  </div>
                ) : (
                  /* ── Escenario: Streaming con RFW ── */
                  <div className="bg-background-50 rounded-2xl border border-background-200 p-5">
                    <div className="flex items-center gap-3 mb-4">
                      <div className="w-10 h-10 rounded-xl bg-emerald-100 flex items-center justify-center">
                        <i className="ri-cloud-line text-emerald-600 text-lg"></i>
                      </div>
                      <div>
                        <h4 className="text-foreground-950 font-bold text-sm">Streaming administrado por RFW</h4>
                        <p className="text-foreground-400 text-[10px]">Tu hosting de streaming es gestionado por nosotros</p>
                      </div>
                      <div className="ml-auto flex items-center gap-1.5">
                        <span className="w-2 h-2 rounded-full bg-emerald-500 animate-pulse"></span>
                        <span className="text-emerald-600 text-xs font-bold">Activo</span>
                      </div>
                    </div>

                    {/* Service cards */}
                    <div className="grid grid-cols-2 sm:grid-cols-3 gap-2 mb-4">
                      {[
                        { icon: 'ri-disc-line', label: 'AutoDJ', desc: 'Programación automática 24/7' },
                        { icon: 'ri-plug-line', label: 'Datos de conexión', desc: 'Información para conectar tu emisor' },
                        { icon: 'ri-global-line', label: 'WebDJ', desc: 'Controlá desde el navegador' },
                        { icon: 'ri-bar-chart-grouped-line', label: 'Estadísticas', desc: 'Oyentes en tiempo real' },
                        { icon: 'ri-customer-service-2-line', label: 'Soporte', desc: 'Asistencia técnica RFW' },
                        { icon: 'ri-admin-line', label: 'Cuenta administrada', desc: 'Todo gestionado por RFW' },
                      ].map((item) => (
                        <div key={item.label} className="bg-background-100 rounded-xl p-3 text-center">
                          <div className="w-8 h-8 rounded-lg bg-emerald-100 flex items-center justify-center mx-auto mb-1.5">
                            <i className={`${item.icon} text-emerald-600 text-sm`}></i>
                          </div>
                          <p className="text-foreground-800 text-[11px] font-semibold">{item.label}</p>
                          <p className="text-foreground-400 text-[9px] mt-0.5">{item.desc}</p>
                        </div>
                      ))}
                    </div>

                    <div className="flex items-center gap-2">
                      <button
                        onClick={() => SwalAlert.toast('Estado del servicio: Todo funcionando correctamente.', 'success')}
                        className="flex items-center justify-center gap-1.5 px-3 py-2.5 rounded-xl bg-background-100 hover:bg-background-200 text-foreground-700 text-xs font-semibold transition-colors cursor-pointer whitespace-nowrap"
                      >
                        <i className="ri-pulse-line"></i>
                        Estado del servicio
                      </button>
                      <button
                        onClick={() => setSignalView('avanzado')}
                        className="flex items-center justify-center gap-1.5 px-3 py-2.5 rounded-xl bg-primary-100 hover:bg-primary-200 text-primary-700 text-xs font-semibold transition-colors cursor-pointer whitespace-nowrap"
                      >
                        <i className="ri-settings-3-line"></i>
                        Configuración
                      </button>
                    </div>
                  </div>
                )}
              </section>

              {/* ═══════════ RECORDATORIO SEGÚN MODELO ═══════════ */}
              {signalView === 'avanzado' && MOCK_HOSTING === 'external' && (
              <div className="bg-amber-50 border-2 border-amber-200 rounded-2xl p-4 flex items-start gap-3">
                <div className="w-10 h-10 rounded-xl bg-amber-200 flex items-center justify-center shrink-0">
                  <i className="ri-information-line text-amber-600 text-lg"></i>
                </div>
                <div>
                  <h3 className="text-amber-800 font-black text-sm uppercase tracking-wide">IMPORTANTE</h3>
                  <p className="text-amber-700 text-xs leading-relaxed mt-0.5">
                    <strong>RadioFrecuenciaWeb NO provee servicio de streaming.</strong> Para transmitir, necesitás conectar tu propio servidor. Configurá la URL de tu stream acá abajo.
                  </p>
                </div>
              </div>
              )}

              {signalView === 'avanzado' && MOCK_HOSTING === 'rfw_hosting' && (
              <div className="bg-emerald-50 border-2 border-emerald-200 rounded-2xl p-4 flex items-start gap-3">
                <div className="w-10 h-10 rounded-xl bg-emerald-200 flex items-center justify-center shrink-0">
                  <i className="ri-shield-check-line text-emerald-600 text-lg"></i>
                </div>
                <div>
                  <h3 className="text-emerald-800 font-black text-sm uppercase tracking-wide">Streaming administrado por RFW</h3>
                  <p className="text-emerald-700 text-xs leading-relaxed mt-0.5">
                    <strong>Tu servidor de streaming es administrado por RFW.</strong> AutoDJ, SSL, estadísticas y soporte incluidos. Si tenés algún problema con tu señal, contactanos desde soporte.
                  </p>
                </div>
              </div>
              )}

              {/* ═══════════ VISTA AVANZADA ═══════════ */}
              {signalView === 'avanzado' && (
              <>
              {/* ═══════════ ESTADO DEL STREAM ═══════════ */}
              <section>
                <h3 className="text-foreground-950 font-bold text-sm mb-3 flex items-center gap-2">
                  <i className="ri-pulse-line text-accent-500"></i>
                  Estado del stream
                </h3>
                <div className="bg-background-50 rounded-2xl border border-background-200 p-5">
                  {/* Status header */}
                  <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4 mb-4">
                    <div className="flex items-center gap-3">
                      <div className={`w-3 h-3 rounded-full ${statusInfo.dot} ${streamState.status === 'online' ? 'animate-pulse' : ''}`}></div>
                      <span className={`px-2.5 py-1 rounded-lg text-xs font-bold ${statusInfo.bg} ${statusInfo.text}`}>
                        {statusInfo.label}
                      </span>
                      <span className="text-foreground-400 text-xs">{activeProvider?.label || '—'} &bull; {streamState.format} {streamState.bitrate}kbps</span>
                    </div>
                    <span className="text-foreground-400 text-[10px]">
                      {streamState.startedAt ? `Inicio: ${fullDateTime(streamState.startedAt)}` : ''}
                    </span>
                  </div>

                  {/* URL actual */}
                  <div className="bg-background-100 rounded-xl p-3 mb-4">
                    <span className="text-foreground-400 text-[9px] font-semibold uppercase tracking-wide">URL del stream</span>
                    <div className="flex items-center gap-2 mt-1">
                      <code className="text-foreground-800 text-xs font-mono break-all flex-1">{streamState.streamUrl}</code>
                      <button
                        onClick={() => {
                          navigator.clipboard.writeText(streamState.streamUrl);
                          SwalAlert.toast('URL copiada', 'success');
                        }}
                        className="w-7 h-7 rounded-lg bg-background-200 flex items-center justify-center cursor-pointer hover:bg-background-300 transition-colors shrink-0"
                      >
                        <i className="ri-file-copy-line text-foreground-400 text-xs"></i>
                      </button>
                    </div>
                  </div>

                  {/* Metrics grid */}
                  <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-6 gap-3">
                    {[
                      { icon: 'ri-user-line', label: 'Oyentes', value: streamState.listeners.toLocaleString(), sub: `Pico ${streamState.peakListeners.toLocaleString()}`, color: 'text-cyan-600', bg: 'bg-cyan-100' },
                      { icon: 'ri-speed-up-line', label: 'Bitrate', value: `${streamState.bitrate} kbps`, sub: streamState.format, color: 'text-amber-600', bg: 'bg-amber-100' },
                      { icon: 'ri-time-line', label: 'Uptime', value: streamState.uptime, sub: 'Sin interrupciones', color: 'text-emerald-600', bg: 'bg-emerald-100' },
                      { icon: 'ri-server-line', label: 'Proveedor', value: activeProvider?.label || '—', sub: activeProvider ? `Puerto ${activeProvider.port}` : '', color: 'text-violet-600', bg: 'bg-violet-100' },
                      { icon: 'ri-link', label: 'URL validada', value: activeProvider?.validated ? 'Sí' : 'No', sub: activeProvider?.lastValidated ? fullDateTime(activeProvider.lastValidated) : '—', color: 'text-rose-600', bg: 'bg-rose-100' },
                      { icon: 'ri-shield-check-line', label: 'Estado', value: statusInfo.label, sub: streamState.status === 'online' ? 'Todo OK' : 'Revisar', color: streamState.status === 'online' ? 'text-emerald-600' : 'text-amber-600', bg: streamState.status === 'online' ? 'bg-emerald-100' : 'bg-amber-100' },
                    ].map((metric) => (
                      <div key={metric.label} className="bg-background-100 rounded-xl p-3">
                        <div className={`w-7 h-7 rounded-lg ${metric.bg} flex items-center justify-center mb-2`}>
                          <i className={`${metric.icon} ${metric.color} text-xs`}></i>
                        </div>
                        <p className="text-foreground-400 text-[10px]">{metric.label}</p>
                        <p className="text-foreground-950 font-bold text-sm">{metric.value}</p>
                        <p className="text-foreground-400 text-[9px]">{metric.sub}</p>
                      </div>
                    ))}
                  </div>
                </div>
              </section>

              {/* ═══════════ CONEXIONES ═══════════ */}
              <section>
                <h3 className="text-foreground-950 font-bold text-sm mb-3 flex items-center gap-2">
                  <i className="ri-plug-line text-accent-500"></i>
                  Conexiones
                </h3>
                <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
                  {providers.map((prov) => {
                    const currentUrl = editingUrl[prov.id] !== undefined ? editingUrl[prov.id] : prov.streamUrl;
                    const isActive = prov.active;
                    return (
                      <div
                        key={prov.id}
                        className={`bg-background-50 rounded-2xl border-2 transition-colors ${
                          isActive ? 'border-emerald-400' : 'border-background-200 hover:border-background-300'
                        }`}
                      >
                        <div className="p-4">
                          {/* Header */}
                          <div className="flex items-center justify-between mb-3">
                            <div className="flex items-center gap-3">
                              <div className={`w-10 h-10 rounded-xl ${prov.bgColor} flex items-center justify-center`}>
                                <i className={`${prov.icon} ${prov.color} text-lg`}></i>
                              </div>
                              <div>
                                <h4 className="text-foreground-950 font-bold text-sm">{prov.label}</h4>
                                <p className="text-foreground-400 text-[10px] line-clamp-1">{prov.description}</p>
                              </div>
                            </div>
                            {isActive && (
                              <span className="px-2 py-0.5 rounded-md bg-emerald-100 text-emerald-700 text-[9px] font-bold whitespace-nowrap">
                                Activo
                              </span>
                            )}
                            {prov.validated === true && !isActive && (
                              <span className="px-2 py-0.5 rounded-md bg-cyan-100 text-cyan-700 text-[9px] font-bold whitespace-nowrap">
                                Validado
                              </span>
                            )}
                          </div>

                          {/* URL Input */}
                          <div className="mb-3">
                            <label className="text-foreground-400 text-[9px] font-semibold uppercase tracking-wide">URL del stream</label>
                            <div className="flex items-center gap-2 mt-1">
                              <input
                                type="text"
                                value={currentUrl}
                                onChange={(e) => handleUrlChange(prov.id, e.target.value)}
                                placeholder={prov.id === 'custom' ? 'https://tu-servidor.com:8000/stream' : prov.streamUrl}
                                className="flex-1 px-3 py-2 rounded-lg bg-background-100 border border-background-200 text-foreground-800 text-xs font-mono placeholder:text-foreground-400 outline-none focus:border-accent-500 focus:ring-1 focus:ring-accent-500/20 transition-colors"
                              />
                            </div>
                          </div>

                          {/* Validation status */}
                          <div className="flex items-center gap-2 mb-3">
                            {prov.validating ? (
                              <span className="flex items-center gap-1.5 text-sky-600 text-[10px] font-semibold">
                                <i className="ri-loader-4-line animate-spin text-xs"></i>
                                Validando...
                              </span>
                            ) : prov.validated === true ? (
                              <span className="flex items-center gap-1.5 text-emerald-600 text-[10px] font-semibold">
                                <i className="ri-check-double-line text-xs"></i>
                                URL validada {prov.lastValidated ? timeAgo(prov.lastValidated) : ''}
                              </span>
                            ) : prov.validated === false ? (
                              <span className="flex items-center gap-1.5 text-rose-500 text-[10px] font-semibold">
                                <i className="ri-close-circle-line text-xs"></i>
                                No se pudo validar
                              </span>
                            ) : (
                              <span className="flex items-center gap-1.5 text-foreground-400 text-[10px]">
                                <i className="ri-question-line text-xs"></i>
                                Sin validar
                              </span>
                            )}
                          </div>

                          {/* Actions */}
                          <div className="flex items-center gap-2">
                            <button
                              onClick={() => handleValidateUrl(prov.id)}
                              disabled={prov.validating || !currentUrl.trim()}
                              className={`flex items-center gap-1.5 px-4 py-2 rounded-xl text-xs font-bold transition-colors cursor-pointer whitespace-nowrap ${
                                prov.validating || !currentUrl.trim()
                                  ? 'bg-background-100 text-foreground-300 cursor-not-allowed'
                                  : 'bg-accent-500 text-background-50 hover:bg-accent-600'
                              }`}
                            >
                              <i className={`text-xs ${prov.validating ? 'ri-loader-4-line animate-spin' : 'ri-check-line'}`}></i>
                              Validar URL
                            </button>
                            {!isActive && (
                              <button
                                onClick={() => handleActivateProvider(prov.id)}
                                disabled={prov.validated !== true}
                                className={`flex items-center gap-1.5 px-4 py-2 rounded-xl text-xs font-semibold transition-colors cursor-pointer whitespace-nowrap ${
                                  prov.validated !== true
                                    ? 'bg-background-100 text-foreground-300 cursor-not-allowed'
                                    : 'bg-background-100 text-foreground-600 hover:bg-background-200'
                                }`}
                              >
                                <i className="ri-play-circle-line text-xs"></i>
                                Activar
                              </button>
                            )}
                            {isActive && (
                              <span className="flex items-center gap-1.5 px-4 py-2 rounded-xl bg-emerald-50 text-emerald-600 text-xs font-semibold whitespace-nowrap">
                                <i className="ri-checkbox-circle-line text-xs"></i>
                                En uso
                              </span>
                            )}
                          </div>
                        </div>
                      </div>
                    );
                  })}
                </div>
              </section>

              {/* ═══════════ RETRANSMISIÓN ═══════════ */}
              <section>
                <h3 className="text-foreground-950 font-bold text-sm mb-3 flex items-center gap-2">
                  <i className="ri-share-forward-line text-accent-500"></i>
                  Retransmisión a redes sociales
                </h3>
                <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
                  {socialStreams.map((social) => (
                    <div
                      key={social.id}
                      className={`bg-background-50 rounded-2xl border-2 transition-colors ${
                        social.active ? social.platform === 'facebook' ? 'border-blue-400' : 'border-red-400' : 'border-background-200'
                      }`}
                    >
                      <div className="p-4">
                        {/* Header */}
                        <div className="flex items-center justify-between mb-3">
                          <div className="flex items-center gap-3">
                            <div className={`w-10 h-10 rounded-xl ${social.active ? social.activeBg : social.bgColor} flex items-center justify-center`}>
                              <i className={`${social.icon} text-lg ${social.active ? 'text-background-50' : social.color}`}></i>
                            </div>
                            <div>
                              <h4 className="text-foreground-950 font-bold text-sm">{social.label}</h4>
                              <p className="text-foreground-400 text-[10px]">
                                {social.platform === 'facebook' ? social.pageName : social.channelName}
                              </p>
                            </div>
                          </div>
                          {/* Toggle */}
                          <button
                            onClick={() => handleSocialToggle(social.id)}
                            disabled={!social.connected}
                            className={`relative w-11 h-6 rounded-full transition-colors cursor-pointer ${
                              !social.connected
                                ? 'bg-background-200 cursor-not-allowed'
                                : social.active
                                  ? social.platform === 'facebook' ? 'bg-blue-500' : 'bg-red-500'
                                  : 'bg-background-300'
                            }`}
                          >
                            <span
                              className={`absolute top-0.5 w-5 h-5 rounded-full bg-background-50 shadow-sm transition-all ${
                                social.active ? 'left-5' : 'left-0.5'
                              }`}
                            ></span>
                          </button>
                        </div>

                        {/* Info */}
                        <div className="bg-background-100 rounded-xl p-3 space-y-2">
                          <div className="flex items-center justify-between text-[10px]">
                            <span className="text-foreground-400">Última emisión</span>
                            <span className="text-foreground-600 font-semibold">{social.lastBroadcast}</span>
                          </div>
                          <div className="flex items-center justify-between text-[10px]">
                            <span className="text-foreground-400">Espectadores</span>
                            <span className="text-foreground-600 font-semibold">{social.viewerCount.toLocaleString()}</span>
                          </div>
                          <div className="flex items-center justify-between text-[10px]">
                            <span className="text-foreground-400">Stream Key</span>
                            <span className="text-foreground-500 font-mono text-[9px]">{social.streamKey.slice(0, 12)}••••</span>
                          </div>
                          <div className="flex items-center justify-between text-[10px]">
                            <span className="text-foreground-400">RTMP URL</span>
                            <span className="text-foreground-500 font-mono text-[9px] truncate max-w-[180px]">{social.rtmpUrl}</span>
                          </div>
                        </div>

                        {/* Status */}
                        <p className="mt-3 text-[10px] text-foreground-400">
                          {!social.connected
                            ? 'No conectado. Vincular desde Social Connect.'
                            : social.active
                              ? `Retransmitiendo en vivo`
                              : 'Listo para activar'}
                        </p>
                      </div>
                    </div>
                  ))}
                </div>
              </section>

              {/* ═══════════ HISTORIAL ═══════════ */}
              <section>
                <h3 className="text-foreground-950 font-bold text-sm mb-3 flex items-center gap-2">
                  <i className="ri-history-line text-accent-500"></i>
                  Historial
                </h3>

                {/* Filter chips */}
                <div className="flex items-center gap-1.5 mb-4 overflow-x-auto pb-1 scrollbar-none">
                  {eventTypes.map((type) => (
                    <button
                      key={type}
                      onClick={() => setHistoryFilter(type)}
                      className={`shrink-0 px-3 py-1.5 rounded-full text-[10px] font-semibold transition-colors cursor-pointer whitespace-nowrap ${
                        historyFilter === type
                          ? 'bg-foreground-950 text-background-50'
                          : 'bg-background-50 text-foreground-500 border border-background-200 hover:bg-background-100'
                      }`}
                    >
                      {typeLabels[type] || type}
                    </button>
                  ))}
                </div>

                {/* Timeline */}
                <div className="space-y-0">
                  {filteredEvents.map((event, idx) => (
                    <div key={event.id} className="flex gap-3">
                      {/* Timeline line + dot */}
                      <div className="flex flex-col items-center shrink-0">
                        <div className={`w-8 h-8 rounded-xl ${event.bgColor} flex items-center justify-center`}>
                          <i className={`${event.icon} ${event.color} text-sm`}></i>
                        </div>
                        {idx < filteredEvents.length - 1 && (
                          <div className="w-0.5 flex-1 bg-background-200 my-1"></div>
                        )}
                      </div>

                      {/* Content */}
                      <div className={`pb-4 flex-1 min-w-0 ${idx === filteredEvents.length - 1 ? '' : ''}`}>
                        <div className="flex items-center gap-2 mb-1 flex-wrap">
                          <h4 className="text-foreground-950 font-bold text-xs">{event.title}</h4>
                          <span className="text-foreground-400 text-[9px] whitespace-nowrap">{timeAgo(event.timestamp)}</span>
                        </div>
                        <p className="text-foreground-500 text-[11px] leading-relaxed">{event.description}</p>
                      </div>
                    </div>
                  ))}
                </div>
              </section>
              </>)}
            </div>
          </div>
        </main>
      </div>


    </div>
  );
}